import * as React from "react";
import {
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MENTION_SOURCE_TYPE, SEARCH_TYPES } from "@/constants";
import { startOfDay, subMonths } from "date-fns";
import { useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Organization } from "@/types/Organization";
import { getCapabilities } from "@/api/endpoints/organization";
import General from "./partials/General";
import Query from "./partials/Query";
import {
  FilterKey,
  getMinReachByChannelTypeDefaultValues,
  MIN_REACH_BY_CHANNEL_TYPE_SOURCES,
  prepareQueryForCreate,
  PROP_TO_PARAM_MAP,
  QueryIn,
} from "./requestUtils";
import { QueryBuilderForm, useQueryBuilderFormSchema } from "./types";
import { getTags } from "@/api/endpoints/tags";
import Staff from "./partials/Staff";
import { SearchQuery } from "@/types/SearchQuery";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { toast } from "sonner";

export enum QueryBuilderSubmitButton {
  Create = "form-qb-create",
  Preview = "form-qb-preview",
  Save = "form-qb-save",
  SaveAndRefresh = "form-qb-save-and-refresh",
}

type QueryBuilderProps = {
  onSubmit: (
    values: QueryBuilderForm,
    submitterName?: QueryBuilderSubmitButton
  ) => void;
  initialValues?: QueryBuilderForm;
  informationNeedError?: string;
};

export default function QueryBuilder({
  onSubmit,
  initialValues,
  informationNeedError,
}: QueryBuilderProps) {
  const { t } = useLingui();

  const queryBuilderFormSchema = useQueryBuilderFormSchema();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // Refs
  const hasInitializedForm = React.useRef<boolean>(false);

  // Server state
  const { data: capabilitiesData } = useQuery({
    queryFn: () => getCapabilities(orgUuid),
    queryKey: [orgUuid, "capabilities"],
    enabled: Boolean(orgUuid),
  });

  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
  });

  // Helpers
  const capabilities = capabilitiesData?.ok ? capabilitiesData.data : undefined;

  const tags = tagsData?.ok
    ? tagsData.data.filter((tag) => tag.slug !== "favorited")
    : [];

  const {
    languages,
    sources,
    audioSources,
    newsSources,
    socialSources,
    industries,
    minReachByChannelTypeSources,
  } = React.useMemo(() => {
    const languages = capabilities?.languages || [];
    const sources = capabilities?.sources || [];
    const audioSources = capabilities?.sources_audio || [];
    const newsSources = capabilities?.sources_news || [];
    const socialSources = capabilities?.sources_social || [];
    const industries = capabilities?.industries || [];
    const uniqueIndustries = [...new Set(industries)];
    const minReachByChannelTypeSources =
      MIN_REACH_BY_CHANNEL_TYPE_SOURCES.filter((source) =>
        sources.includes(source)
      );

    return {
      languages,
      sources,
      audioSources,
      newsSources,
      socialSources,
      minReachByChannelTypeSources,
      industries: uniqueIndustries,
    };
  }, [capabilities]);

  const DEFAULT_QUERY: QueryIn = React.useMemo(() => {
    return {
      keywords: [],
      nearKeywords: [],
      notNearKeywords: [],
      notNearDistance: 10,
      nearDistance: 10,
      languages,
      sources,
      mentionTypes: MENTION_SOURCE_TYPE.ALL,
    };
  }, [languages, sources]);

  // Form state
  const form = useForm<QueryBuilderForm>({
    resolver: zodResolver(queryBuilderFormSchema),
    defaultValues: initialValues ?? {
      title: "",
      type: SEARCH_TYPES.BRAND,
      industry: null,
      startDate: startOfDay(subMonths(new Date(), 1)),
      endDate: undefined,
      queries: [],
      informationNeed: "",
    },
  });

  const startDate = useWatch({
    control: form.control,
    name: "startDate",
  });

  const endDate = useWatch({
    control: form.control,
    name: "endDate",
  });

  const queriesField = useFieldArray({
    control: form.control,
    name: "queries",
  });

  const queries = useWatch({
    control: form.control,
    name: "queries",
  });

  const isEditing = Boolean(initialValues);

  // Side-effects
  React.useEffect(() => {
    if (capabilities && !hasInitializedForm.current && !initialValues) {
      hasInitializedForm.current = true;
      queriesField.append({
        ...DEFAULT_QUERY,
        minReachByChannelType: getMinReachByChannelTypeDefaultValues(
          minReachByChannelTypeSources
        ),
      });
    }
  }, [
    DEFAULT_QUERY,
    capabilities,
    queriesField,
    initialValues,
    minReachByChannelTypeSources,
  ]);

  // Helpers
  function resetQueries() {
    form.setValue("queries", []);
  }

  // Handlers
  function handleApplySearchTemplate(
    queries: Array<Omit<SearchQuery, "uuid"> & { uuid?: string | null }>
  ) {
    resetQueries();

    const camelCaseQueryKeys = Object.keys(PROP_TO_PARAM_MAP) as FilterKey[];

    const camelCaseQueries: QueryIn[] = queries.map((query) => {
      const q = camelCaseQueryKeys.reduce(
        (all, each) => {
          all[each] = query[PROP_TO_PARAM_MAP[each]];
          return all;
        },
        {} as Record<FilterKey, unknown>
      );

      return q as QueryIn;
    });

    camelCaseQueries.forEach((query) => {
      queriesField.append(query);
    });
  }

  async function handleCopyQueries() {
    try {
      const preparedQueries = queries.map(prepareQueryForCreate);
      const jsonString = JSON.stringify({ queries: preparedQueries }, null, 2);
      await navigator.clipboard.writeText(jsonString);
      toast.success(t`JSON kopierades till urklipp`);
    } catch {
      toast.error(t`Kunde inte kopiera JSON`);
    }
  }

  const handleSubmit: SubmitHandler<QueryBuilderForm> = function (values, e) {
    const nativeEvent = e?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent?.submitter as HTMLButtonElement;
    const submitterName = submitter?.name as QueryBuilderSubmitButton;
    onSubmit(values, submitterName);
  };

  return (
    <div className="flex flex-col gap-6">
      <AccessControl level={ACCESS_LEVELS.STAFF}>
        <Staff
          onApplySearchQueries={handleApplySearchTemplate}
          onCopyQueries={handleCopyQueries}
        />
      </AccessControl>

      <form
        id="form-qb"
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-6"
      >
        <General
          control={form.control}
          setValue={form.setValue}
          startDate={startDate}
          endDate={endDate}
          industries={industries}
          informationNeedError={informationNeedError}
          isEditing={isEditing}
        />

        {queriesField.fields.map((query, queryIndex) => (
          <Query
            minReachByChannelTypeSources={minReachByChannelTypeSources}
            socialSources={socialSources}
            defaultQuery={DEFAULT_QUERY}
            queriesField={queriesField}
            audioSources={audioSources}
            newsSources={newsSources}
            languages={languages}
            index={queryIndex}
            queries={queries}
            sources={sources}
            key={query.id}
            form={form}
            tags={tags}
          />
        ))}
      </form>
    </div>
  );
}
